import { cn } from "@/lib/utils";
import { minutesToTime, type MoveDragState } from "./pricingGridUtils";

interface RuleSpan {
  rule: any;
  startSlot: number;
  endSlot: number;
}

interface PricingGridRowProps {
  dayIndex: number;
  dayShortName: string;
  totalSlots: number;
  startMinutes: number;
  cellMinutes: number;
  cellRuleMap: Record<string, any>;
  spans: RuleSpan[];
  selectedRuleIds: Set<string>;
  editingRuleId?: string;
  isSelected: (day: number, slot: number) => boolean;
  onMouseDown: (day: number, slot: number, e: React.MouseEvent) => void;
  onMouseEnter: (day: number, slot: number) => void;
  onRuleBlockClick: (e: React.MouseEvent, rule: any) => void;
  onResizeDragStart: (e: React.MouseEvent, rule: any, edge: "left" | "right", dayIndex: number) => void;
  onMoveDragStart: (e: React.MouseEvent, rule: any, day: number) => void;
  moveDrag: MoveDragState | null;
  isMoving: boolean;
  overlapWarning: string | null;
  canMove: boolean;
}

export default function PricingGridRow({
  dayIndex,
  dayShortName,
  totalSlots,
  startMinutes,
  cellMinutes,
  cellRuleMap,
  spans,
  selectedRuleIds,
  editingRuleId,
  isSelected,
  onMouseDown,
  onMouseEnter,
  onRuleBlockClick,
  onResizeDragStart,
  onMoveDragStart,
  moveDrag,
  isMoving,
  overlapWarning,
  canMove,
}: PricingGridRowProps) {
  const isoDay = dayIndex + 1;

  return (
    <div className="flex border-t" data-day-row>
      <div className="w-16 shrink-0 py-2 px-2 text-xs font-medium text-muted-foreground flex items-center">
        {dayShortName}
      </div>
      <div className="flex-1 flex relative">
        {/* Cell grid */}
        {Array.from({ length: totalSlots }).map((_, slot) => {
          const key = `${isoDay}-${slot}`;
          const rule = cellRuleMap[key];
          const selected = isSelected(isoDay, slot);
          const minute = startMinutes + slot * cellMinutes;
          const isHourBorder = minute % 60 === 0;

          return (
            <div
              key={slot}
              data-slot={slot}
              className={cn(
                "flex-1 h-12 flex items-center justify-center text-[9px] cursor-crosshair transition-colors relative z-10",
                isHourBorder ? "border-l border-border/60" : "border-l border-border/20",
                slot === totalSlots - 1 && "border-r border-border/60",
                !rule && !selected && "border-dashed",
                selected && "bg-primary/20 ring-1 ring-inset ring-primary/40",
              )}
              style={
                rule && !selected
                  ? { backgroundColor: rule.color + "18" }
                  : undefined
              }
              onMouseDown={(e) => onMouseDown(isoDay, slot, e)}
              onMouseEnter={() => onMouseEnter(isoDay, slot)}
            >
              {!rule && !selected && (
                <span className="text-muted-foreground/30 pointer-events-none" style={{ fontSize: "8px" }}>
                  –
                </span>
              )}
            </div>
          );
        })}

        {/* Rule block overlays */}
        {spans.map((span) => {
          const left = (span.startSlot / totalSlots) * 100;
          const width = ((span.endSlot - span.startSlot) / totalSlots) * 100;
          const rule = span.rule;
          const isEditing = editingRuleId === rule.id;
          const isSelectedForMerge = selectedRuleIds.has(rule.id);
          const isBeingMoved = moveDrag?.ruleId === rule.id && isMoving;

          return (
            <div
              key={`${rule.id}-${isoDay}`}
              data-rule-block
              className={cn(
                "absolute top-0.5 bottom-0.5 rounded flex items-center px-1.5 transition-all group overflow-hidden z-20",
                isEditing && "ring-2 ring-primary",
                isSelectedForMerge && "ring-2 ring-accent-foreground",
                isBeingMoved && "opacity-30",
                !isMoving && "hover:brightness-110",
              )}
              style={{
                left: `${left}%`,
                width: `${width}%`,
                backgroundColor: rule.color + "30",
                borderLeft: `3px solid ${rule.color}`,
              }}
              onClick={(e) => onRuleBlockClick(e, rule)}
            >
              {/* Left resize handle */}
              <div
                className="absolute left-0 top-0 bottom-0 w-2 cursor-col-resize opacity-0 group-hover:opacity-100 hover:bg-foreground/20 transition-opacity z-30"
                onMouseDown={(e) => onResizeDragStart(e, rule, "left", dayIndex)}
              />

              {/* Center move handle */}
              {canMove && (
                <div
                  className="absolute left-2 right-2 top-0 bottom-0 cursor-grab active:cursor-grabbing z-25"
                  onMouseDown={(e) => onMoveDragStart(e, rule, isoDay)}
                />
              )}

              <div className="flex-1 overflow-hidden min-w-0 pointer-events-none">
                <p className="text-[10px] font-semibold truncate" style={{ color: rule.color }}>
                  {rule.name}
                </p>
                <p className="text-[9px] font-medium truncate" style={{ color: rule.color }}>
                  {rule.price_per_hour} kr/h
                </p>
              </div>

              {/* Right resize handle */}
              <div
                className="absolute right-0 top-0 bottom-0 w-2 cursor-col-resize opacity-0 group-hover:opacity-100 hover:bg-foreground/20 transition-opacity z-30"
                onMouseDown={(e) => onResizeDragStart(e, rule, "right", dayIndex)}
              />
            </div>
          );
        })}

        {/* Ghost block for move drag */}
        {moveDrag && isMoving && moveDrag.currentDay === isoDay && (() => {
          const ghostStartSlot = (moveDrag.currentStartMin - startMinutes) / cellMinutes;
          const ghostEndSlot = ghostStartSlot + moveDrag.durationMin / cellMinutes;
          const ghostLeft = (ghostStartSlot / totalSlots) * 100;
          const ghostWidth = ((ghostEndSlot - ghostStartSlot) / totalSlots) * 100;
          const rule = moveDrag.rule;
          return (
            <div
              className={cn(
                "absolute top-0.5 bottom-0.5 rounded flex items-center px-1.5 z-30 pointer-events-none border-2 border-dashed",
                overlapWarning ? "border-destructive bg-destructive/10" : "border-primary bg-primary/10"
              )}
              style={{
                left: `${ghostLeft}%`,
                width: `${ghostWidth}%`,
              }}
            >
              <div className="flex-1 overflow-hidden min-w-0">
                <p className="text-[10px] font-semibold truncate text-foreground">
                  {rule.name}
                </p>
                <p className="text-[9px] font-medium truncate text-muted-foreground">
                  {minutesToTime(moveDrag.currentStartMin)}–{minutesToTime(moveDrag.currentStartMin + moveDrag.durationMin)}
                </p>
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
}
